import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Gift, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const OPT_OUT_KEY = "fitjong:partnerPerkOptOut";

export function partnerPerkOptedOut() {
  try { return typeof localStorage !== "undefined" && localStorage.getItem(OPT_OUT_KEY) === "1"; }
  catch (_) { return false; }
}

/**
 * In-game "partner perk" reward popup (e.g. 10% off VitaStrong). Appears
 * occasionally after a win as a little bonus reward instead of living
 * permanently on the Home screen. Offers Claim (→ /rewards), Maybe later,
 * and a "Don't show again" opt-out persisted to localStorage.
 *
 * Props: open, onClose
 */
export default function PartnerPerkPopup({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const optOut = () => {
    try { localStorage.setItem(OPT_OUT_KEY, "1"); } catch (_) { /* ignore */ }
    onCloseRef.current?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <div
          data-testid="partner-perk-popup"
          className="fixed inset-0 z-[250] flex items-center justify-center px-5 bg-[#2C332A]/55 backdrop-blur-[3px] pointer-events-auto"
          onClick={() => onCloseRef.current?.()}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.86 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -14, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[330px] rounded-3xl overflow-hidden shadow-2xl px-6 py-6 text-center"
            style={{ background: "linear-gradient(160deg,#2C332A 0%,#3A2320 100%)", border: "1px solid rgba(224,86,73,0.3)" }}
          >
            <button
              data-testid="partner-perk-close"
              onClick={() => onCloseRef.current?.()}
              className="absolute top-3 right-3 p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X size={16} strokeWidth={2} />
            </button>

            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: [0, 1, 0], y: -20 - i * 10, x: (i % 2 ? 1 : -1) * (26 + i * 12), scale: 1 }}
                transition={{ duration: 1.5, delay: 0.15 + i * 0.1 }}
                className="absolute top-6 left-1/2 -translate-x-1/2 text-[#E8A365] pointer-events-none"
              >
                <Sparkles size={15} strokeWidth={2.2} />
              </motion.div>
            ))}

            <motion.div
              initial={{ scale: 0.4, rotate: -18 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 240, damping: 13, delay: 0.05 }}
              className="w-16 h-16 mx-auto rounded-2xl bg-[#E05649]/20 grid place-items-center"
            >
              <Gift size={30} className="text-[#E8A365]" strokeWidth={1.8} />
            </motion.div>

            <p className="text-[10px] uppercase tracking-[0.24em] text-[#E8A365] font-bold mt-4">
              {t("rewards.popup_tag", "Surprise reward")}
            </p>
            <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-semibold text-white mt-1 leading-tight">
              {t("rewards.dash_card_title")}
            </h2>
            <p className="text-xs text-white/65 mt-1.5">{t("rewards.dash_card_sub")}</p>

            <button
              data-testid="partner-perk-claim"
              onClick={() => { onCloseRef.current?.(); navigate("/rewards"); }}
              className="mt-5 w-full h-11 rounded-full bg-[#E8A365] hover:bg-[#D9924E] text-[#2C332A] font-semibold text-sm"
            >
              {t("rewards.popup_claim", "Claim reward")}
            </button>
            <div className="flex items-center justify-between mt-3 px-1">
              <button
                data-testid="partner-perk-later"
                onClick={() => onCloseRef.current?.()}
                className="text-xs text-white/60 hover:text-white/90"
              >
                {t("rewards.popup_later", "Maybe later")}
              </button>
              <button
                data-testid="partner-perk-optout"
                onClick={optOut}
                className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2"
              >
                {t("rewards.popup_optout", "Don't show again")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
